/**
 * Editor de plantillas en 3 pasos (`plantillas/nueva` y `plantillas/:id/editar`).
 *
 * El editor en sí vive en `components/editor-plantilla/EditorPlantilla.tsx` (lo usa también la
 * plantilla de una campaña, `CampanaPlantillaPage`) y la carga en `hooks/useCargaEditorPlantilla.ts`.
 * Esta página es el envoltorio: los estados de carga y error, y las navegaciones de siempre, que el
 * editor le avisa por callbacks.
 *
 * El idioma NO se elige acá: se muestra el de la marca y se cambia en Configuración → Envío
 * (decisión C7 / D-C).
 */

import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useCuentaOficial } from "../contexto";
import { useCargaEditorPlantilla } from "../hooks/useCargaEditorPlantilla";
import { EditorPlantilla } from "../components/editor-plantilla/EditorPlantilla";
import { Tarjeta } from "../components/Tarjeta";

const R = "/activa/oficial";
const enc = encodeURIComponent;

export default function PlantillaEditorPage() {
  const { id: idRuta } = useParams();
  const [params] = useSearchParams();
  const modelo = params.get("modelo");
  const navigate = useNavigate();
  const { cargando: cargandoCuenta, config, error: errorCuenta, recargar } = useCuentaOficial();
  const { carga, campos, camposFallaron, reintentar, marcarCreada } = useCargaEditorPlantilla({
    id: idRuta,
    modelo,
    alNoSerBorrador: (p) => navigate(`${R}/plantillas/${enc(p.id)}`, { replace: true, state: { aviso: "Esta plantilla ya se mandó a Meta: para cambiarla, duplicala." } }),
  });

  if (cargandoCuenta && !config) return <Esqueleto />;
  if (!config) {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la configuración de la marca.</p>
        <p className="text-sm text-slate-600 mt-1">{errorCuenta || "Sin la configuración no sabemos el texto de baja ni el idioma: no se puede armar la plantilla."}</p>
        <button type="button" onClick={() => void recargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  }
  if (carga.estado === "cargando") return <Esqueleto />;
  if (carga.estado === "no_existe") {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-slate-800">No encontramos esta plantilla.</p>
        <Link to={`${R}/plantillas`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <ArrowLeft size={14} /> Volver a las plantillas
        </Link>
      </Tarjeta>
    );
  }
  if (carga.estado === "error") {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la plantilla.</p>
        <p className="text-sm text-slate-600 mt-1">{carga.error}</p>
        <button type="button" onClick={reintentar} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  }

  return (
    <EditorPlantilla
      key={carga.clave}
      modo="pagina"
      inicial={carga.inicial}
      idInicial={carga.id}
      avisoInicial={carga.aviso}
      config={config}
      campos={campos}
      camposFallaron={camposFallaron}
      // Al crearla, la URL pasa de /nueva a /:id/editar sin volver a traerla (`marcarCreada`).
      onCreada={(nuevo) => { marcarCreada(nuevo); navigate(`${R}/plantillas/${enc(nuevo)}/editar`, { replace: true }); }}
      onEnviada={(p) => navigate(`${R}/plantillas/${enc(p.id)}`, { state: { aviso: "Mandada a Meta. Te avisamos acá cuando cambie el estado." } })}
      // Si el Content se creó pero la aprobación falló, la plantilla quedó «Borrador en Twilio»: su
      // detalle ofrece «Reintentar envío a aprobación» sin crear otro Content.
      onUnsubmitted={(pid, texto) => navigate(`${R}/plantillas/${enc(pid)}`, { state: { aviso: texto } })}
      onDuplicada={(copia) => navigate(`${R}/plantillas/${enc(copia)}/editar`)}
    />
  );
}

function Esqueleto() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Cargando la plantilla">
      <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
      <div className="grid lg:grid-cols-[1fr_300px] gap-4">
        <div className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
      </div>
    </div>
  );
}
